import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Car, DollarSign, Clock, CheckCircle, MessageSquare } from "lucide-react";

const CareersPage = () => {
  const requirements = [
    "Valid Class 3/3A driving license",
    "Minimum 2 years of driving experience",
    "Clean driving record",
    "Professional and courteous demeanor",
    "Flexible schedule availability",
    "Good knowledge of local routes"
  ];

  const benefits = [
    "Competitive pay rates",
    "Flexible working hours",
    "Weekly payouts",
    "Professional training provided",
    "Supportive team environment",
    "Growth opportunities"
  ];

  const handleApply = () => {
    window.open("https://wa.me/6588921053?text=Hi, I'm interested in applying for a driver position at Valet Find Me", "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-in">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-6">
              <Car className="w-10 h-10 text-yellow-700" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Join Our Team of
              <span className="block text-yellow-600 mt-2">Professional Drivers</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              We're looking for experienced, reliable drivers to join Valet Find Me. 
              Help us provide safe, professional valet services while earning competitive pay.
            </p>
          </div>

          {/* Requirements & Benefits Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Requirements Card */}
            <Card className="border-2 border-yellow-300 shadow-lg card-hover bg-gradient-to-br from-white to-yellow-50">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-4">
                    <CheckCircle className="w-6 h-6 text-amber-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Requirements</h2>
                </div>
                <ul className="space-y-4">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Benefits Card */}
            <Card className="border-2 border-yellow-300 shadow-lg card-hover bg-gradient-to-br from-white to-amber-50">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
                    <DollarSign className="w-6 h-6 text-yellow-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Benefits</h2>
                </div>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Positions Available */}
          <div className="bg-white rounded-2xl shadow-xl p-10 mb-16 border-2 border-yellow-200">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Open Positions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Full-Time Position */}
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-8 rounded-xl border-2 border-yellow-300">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-yellow-700 mr-3" />
                  <h3 className="text-2xl font-bold text-slate-900">Full-Time Driver</h3>
                </div>
                <p className="text-slate-700 mb-4">Looking for dedicated drivers to join our team on a full-time basis</p>
                <div className="space-y-2 text-slate-600">
                  <p>• Competitive monthly salary</p>
                  <p>• Fixed schedule with overtime pay</p>
                  <p>• Medical benefits included</p>
                </div>
              </div>

              {/* Part-Time Position */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-8 rounded-xl border-2 border-amber-300">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-amber-700 mr-3" />
                  <h3 className="text-2xl font-bold text-slate-900">Part-Time Driver</h3>
                </div>
                <p className="text-slate-700 mb-4">Perfect for those looking for flexible working hours</p>
                <div className="space-y-2 text-slate-600">
                  <p>• Flexible schedule</p>
                  <p>• Competitive per-trip rates</p>
                  <p>• Choose your own hours</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-yellow-400 to-amber-400 rounded-2xl shadow-2xl p-12 text-center">
            <MessageSquare className="w-16 h-16 text-black mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Ready to Join Us?
            </h2>
            <p className="text-xl text-slate-800 mb-8 max-w-2xl mx-auto">
              Start your journey with Valet Find Me today. Contact us on WhatsApp to apply.
            </p>
            <Button 
              onClick={handleApply}
              size="lg"
              className="bg-black text-white hover:bg-slate-800 text-lg px-12 py-6 h-auto font-semibold shadow-lg"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Apply via WhatsApp
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
